// src/pages/DestinationSelector.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { orgAPI } from '../services/api';
import { User, Plus, ChevronRight, Briefcase } from 'lucide-react';
import { DelBtn } from '../components/shared/UI';

const LOGIN_DEFAULT_KEY = 'loginDefault';

export const getLoginDefault = () => localStorage.getItem(LOGIN_DEFAULT_KEY);
export const clearLoginDefault = () => localStorage.removeItem(LOGIN_DEFAULT_KEY);

export const DestinationSelector = () => {
  const { user, orgs, refreshOrgs, switchOrg } = useAuth();
  const navigate = useNavigate();
  const [rememberChoice, setRememberChoice] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const savedDefault = getLoginDefault();

  const goPersonal = () => {
    if (rememberChoice) localStorage.setItem(LOGIN_DEFAULT_KEY, 'seeker');
    navigate('/seeker');
  };

  const goOrg = (org) => {
    if (rememberChoice) {
      localStorage.setItem(LOGIN_DEFAULT_KEY, `org:${org.id}`);
      localStorage.setItem('activeOrgId', org.id);
    }
    switchOrg(org);
    navigate('/employer');
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    const name = newOrgName.trim();
    if (!name) return;
    setSaving(true);
    setError('');
    try {
      await orgAPI.create({ name });
      await refreshOrgs();
      setCreating(false);
      setNewOrgName('');
      navigate('/employer');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create organisation');
    } finally {
      setSaving(false);
    }
  };

  const initials = (name) => name?.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '?';

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div style={{ width: '100%', maxWidth: 480 }}>

        {/* Saved default banner */}
        {savedDefault && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: '#EBF3FC', border: '1px solid #B3D1F5', borderRadius: 8,
            padding: '10px 14px', marginBottom: 20, gap: 10,
          }}>
            <div style={{ fontSize: 12, color: '#1a4f8a' }}>
              You have a saved default destination. You were sent here because it's no longer available,
              or you navigated here manually.
            </div>
            <DelBtn onClick={() => { clearLoginDefault(); }} size={10} title="Clear" />
          </div>
        )}

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'var(--primary)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
          }}>
            <Briefcase size={26} color="white" />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)', margin: '0 0 6px' }}>
            Where would you like to go?
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-mid)', margin: 0 }}>
            Welcome back{user?.firstName ? `, ${user.firstName}` : ''}.
          </p>
        </div>

        {/* Personal profile card */}
        <button onClick={goPersonal} style={cardStyle}>
          <div style={iconWrap('#EBF3FC')}>
            <User size={20} color="#3D80CC" />
          </div>
          <div style={{ flex: 1, textAlign: 'left' }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>Personal Profile</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              Job seeker profile, CV builder &amp; job applications
            </div>
          </div>
          <ChevronRight size={16} color="var(--text-muted)" />
        </button>

        {/* Org cards */}
        {orgs.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--secondary)',
              textTransform: 'uppercase', letterSpacing: '0.06em',
              marginBottom: 8, marginTop: 20 }}>
              Your Organisations
            </div>
            {orgs.map((org) => (
              <button key={org.id} onClick={() => goOrg(org)} style={{ ...cardStyle, marginBottom: 8 }}>
                <div style={{
                  ...iconWrap('#FFF3E0'),
                  background: 'var(--primary)',
                  color: 'white', fontWeight: 700, fontSize: 13,
                }}>
                  {initials(org.name)}
                </div>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>{org.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {org.myOrgRole === 'OWNER' ? 'Owner' : 'Billing access'}
                    {org.groupProfiles?.length
                      ? ` · ${org.groupProfiles.length} profile${org.groupProfiles.length !== 1 ? 's' : ''}`
                      : ''}
                  </div>
                </div>
                <ChevronRight size={16} color="var(--text-muted)" />
              </button>
            ))}
          </div>
        )}

        {/* Create org */}
        <div style={{ marginTop: orgs.length ? 12 : 20 }}>
          {creating ? (
            <form onSubmit={handleCreateOrg} style={{
              background: 'white', borderRadius: 10, border: '1px solid var(--border)',
              padding: 16,
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
                Create a new organisation
              </div>
              <input
                autoFocus
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: 6,
                  border: '1px solid var(--border)', fontSize: 13,
                  background: 'white', color: 'var(--text)', outline: 'none',
                  boxSizing: 'border-box', marginBottom: 8,
                }}
                placeholder="Organisation name"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
              />
              {error && (
                <div style={{ fontSize: 12, color: '#CC0000', marginBottom: 8 }}>{error}</div>
              )}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-ghost btn-sm"
                  onClick={() => { setCreating(false); setError(''); setNewOrgName(''); }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !newOrgName.trim()}>
                  {saving ? 'Creating…' : 'Create'}
                </button>
              </div>
            </form>
          ) : (
            <button onClick={() => setCreating(true)} style={{
              ...cardStyle,
              border: '1.5px dashed var(--border)',
              background: 'transparent',
            }}>
              <div style={iconWrap('#F5F5F5')}>
                <Plus size={18} color="var(--text-mid)" />
              </div>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-mid)' }}>
                  Create New Organisation
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  Set up a group account for your company or team
                </div>
              </div>
            </button>
          )}
        </div>

        {/* Remember choice */}
        <label style={{
          display: 'flex', alignItems: 'center', gap: 8, marginTop: 24,
          cursor: 'pointer', userSelect: 'none',
        }}>
          <input
            type="checkbox"
            checked={rememberChoice}
            onChange={(e) => setRememberChoice(e.target.checked)}
            style={{ width: 14, height: 14, cursor: 'pointer', accentColor: '#3D80CC' }}
          />
          <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>
            Remember my choice — skip this page on future logins
          </span>
        </label>

      </div>
    </div>
  );
};

const cardStyle = {
  display: 'flex', alignItems: 'center', gap: 14,
  width: '100%', padding: '14px 16px', borderRadius: 10,
  border: '1px solid var(--border)', background: 'white',
  cursor: 'pointer', marginBottom: 8, transition: 'border-color 0.15s, box-shadow 0.15s',
  textDecoration: 'none',
};

const iconWrap = (bg) => ({
  width: 40, height: 40, borderRadius: 10, background: bg,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0, fontSize: 13, fontWeight: 700,
});
